/**
 * Reglas puras del merge de filas del Consolidado en el Excel fusionado
 * (multi-liquidación) de Liquidaciones Agencia.
 *
 * ERR-02a (2026-09-29): `mergeConsolidadoByContainer`, embebida en el componente,
 * tenía un `if (!key) continue;` que descartaba TODA fila sin contenedor (Dus No
 * Embarcado, O/S sin contenedor tipo "Correccion de BL") del fusionado de varias
 * liquidaciones. Se extrae acá como función pura para poder testear los casos
 * borde sin levantar el componente completo.
 *
 * Reglas (ver spec `agency-liquidation-excel`, requirement "Containerless Rows
 * Kept in the Multi-Liquidation Consolidado"):
 *  - Una fila con contenedor real (no vacío, no 'CANCELADO') se fusiona con otra
 *    fila del MISMO contenedor normalizado, venga de la liquidación que venga
 *    (comportamiento actual, sin cambios).
 *  - Una fila SIN contenedor (vacío/NULL) o con el literal `CANCELADO` (ERR-02b,
 *    el backend ahora emite esa fila en vez de perderla) NUNCA se fusiona con
 *    otra fila — ni de la misma liquidación ni de otra. Cada una recibe una clave
 *    sintética única `SINCNTR|<índiceLiquidación>|<índiceFila>`.
 *  - El normalizador de contenedor replica la convención del módulo:
 *    `trim().toUpperCase().replace(/\s+/g, '')`.
 */

/** Fila del Consolidado. Sólo se tipan los campos que el merge necesita. */
export interface ConsolidadoRow {
    contenedor?: string | null;
    nroLiquidacion?: string;
    [key: string]: unknown;
}

/**
 * Campos monetarios/cantidad que se SUMAN cuando dos filas del mismo contenedor
 * se fusionan. Copiado tal cual de la lista que ya usaba el componente.
 */
export const CONSOLIDADO_NUMERIC_KEYS: readonly string[] = [
    'plts',
    'agenciamiento',
    'dusLegalizado',
    'securityFeeContenedor',
    'ucco',
    'uccoChinaEur',
    'uccoFgn',
    'extraUcco',
    'fito',
    'extraFito',
    'correccionBl',
    'controlSello',
    'gateOut',
    'gateIn',
    'dusNoLegalizado',
    'costoTransporte',
    'osVenta',
    'servicioGateOut',
    'sobrestadiaPuerto',
    'porteos',
    'sobrestadiaPlanta',
    'falsoFleteDeposito',
    'falsoFletePlanta',
    'habilitacionDeposito',
    'retiroCruzado',
    'almacenaje',
    'interplanta',
    'totalVentaAgenciamiento',
    'totalTraspasoAgenciamiento',
    'totalVentaTransporte',
    'totalTraspasoTransporte',
    'totalVenta',
    'totalTraspaso',
    'totalGeneral',
];

/** Literal que el backend usa para una O/S de agenciamiento sin contenedor (ERR-02b). */
export const CONTENEDOR_CANCELADO = 'CANCELADO';

/**
 * Normaliza un número de contenedor: `trim().toUpperCase().replace(/\s+/g, '')`.
 * Misma convención que el resto del módulo (ver CLAUDE.md, regla 8).
 */
export function normalizarContenedor(
    contenedor: string | null | undefined
): string {
    return (contenedor ?? '').trim().toUpperCase().replace(/\s+/g, '');
}

/** True si la fila NO tiene un contenedor real: vacía, NULL o 'CANCELADO'. */
export function esFilaSinContenedor(
    contenedor: string | null | undefined
): boolean {
    const normalizado = normalizarContenedor(contenedor);
    return normalizado === '' || normalizado === CONTENEDOR_CANCELADO;
}

/**
 * Fusiona las filas del Consolidado de varias liquidaciones por número de
 * contenedor, sumando los campos numéricos cuando coincide.
 *
 * Recibe las filas AGRUPADAS por liquidación (no un único array ya aplanado):
 * es la única forma de asignarle a cada fila sin contenedor una clave que no
 * choque con la de otra liquidación.
 */
export function mergeConsolidadoByContainer(
    rowsByLiquidacion: readonly (readonly ConsolidadoRow[])[]
): ConsolidadoRow[] {
    const map = new Map<string, ConsolidadoRow>();

    rowsByLiquidacion.forEach((rows, liqIndex) => {
        rows.forEach((row, rowIndex) => {
            const key = esFilaSinContenedor(row.contenedor)
                ? `SINCNTR|${liqIndex}|${rowIndex}`
                : normalizarContenedor(row.contenedor);

            const existing = map.get(key);
            if (!existing) {
                map.set(key, { ...row });
                return;
            }

            for (const numericKey of CONSOLIDADO_NUMERIC_KEYS) {
                const previo = Number(existing[numericKey]) || 0;
                const nuevo = Number(row[numericKey]) || 0;
                existing[numericKey] = Math.round((previo + nuevo) * 100) / 100;
            }
            if (
                row.nroLiquidacion &&
                existing.nroLiquidacion !== row.nroLiquidacion
            ) {
                existing.nroLiquidacion = `${existing.nroLiquidacion}/${row.nroLiquidacion}`;
            }
        });
    });

    return Array.from(map.values());
}
